/* ═══ AgCollapse — Anagogia Design System ═══ */

import { useEffect, useRef, useState } from 'react';
import styles from './AgCollapse.module.css';

interface AgCollapseProps {
  /** Whether the content is visible */
  open: boolean;
  /** Content to reveal / hide */
  children: React.ReactNode;
  className?: string;
}

/**
 * Conteneur à dépliage animé en hauteur.
 * @category Overlays
 */
function AgCollapse({ open, children, className }: AgCollapseProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(
    open ? undefined : 0,
  );
  const [transitionDone, setTransitionDone] = useState(open);
  /* État d'ouverture déjà traité : au montage, rien n'a changé, donc rien à animer. */
  const lastOpen = useRef(open);

  /* Measure content height whenever children or open changes */
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    /* Monté ouvert (ou effet rejoué par StrictMode) : aucune transition n'aura lieu,
       donc aucun `transitionend` ne lèverait le plafond — le contenu resterait coupé
       à sa hauteur du montage (section chargée après coup ; fumée C18.22, 23/09). */
    if (open === lastOpen.current && open) return;
    lastOpen.current = open;

    if (open) {
      setTransitionDone(false);
      /* Read the scroll height and set it as the max-height target */
      const height = el.scrollHeight;
      setMaxHeight(height);
    } else {
      /* First, ensure max-height is set to the current height (not "auto") so
         the browser can animate from a concrete value down to 0 */
      const height = el.scrollHeight;
      setMaxHeight(height);

      /* Force a reflow, then collapse to 0 */
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      el.offsetHeight;
      requestAnimationFrame(() => {
        setMaxHeight(0);
      });
      setTransitionDone(false);
    }
  }, [open]);

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    /* Only react to our own max-height transition */
    if (e.propertyName !== 'max-height') return;
    if (open) {
      /* Remove max-height cap so dynamic content can grow freely */
      setTransitionDone(true);
    }
  };

  const rootClass = [
    styles.collapse,
    open ? styles.expanded : styles.collapsed,
    open && transitionDone && styles.expandedDone,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={contentRef}
      className={rootClass}
      style={{
        maxHeight:
          transitionDone && open ? undefined : maxHeight !== undefined ? `${maxHeight}px` : undefined,
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      {children}
    </div>
  );
}

export { AgCollapse };
export type { AgCollapseProps };
export default AgCollapse;
